import { resolveFeatureGate } from 'pb-billing-ui/feature-gates.ts';
import { licenseTierRank } from '~/types/licenses/tiers';
import type { License, Person } from '~/types/person/person';

/**
 * What a member must do before a licensed surface can act for them.
 *
 * - `allowed`       — the seat assigned to them is live and covers the feature.
 * - `needs_upgrade` — live seat, but the plan does not reach this feature.
 * - `needs_gift`    — no live assigned seat, yet they own a spare unassigned one: assign it,
 *                     do not sell them a second.
 * - `needs_purchase`— no live seat and no spare.
 */
export type LicenseGate = 'allowed' | 'needs_gift' | 'needs_purchase' | 'needs_upgrade';

/** The person the gate is asked about — the loaded/impersonated person, never the buyer. */
type LicenseHolder = Pick<Person, 'license' | 'licenses'> | null | undefined;

/**
 * Licensed = the seat ASSIGNED to this person (`license`), and not lapsed.
 *
 * `licenses` is a different question — the seats the person BOUGHT, for gifting. An
 * employer-provisioned employee owns nothing, so `licenses` is empty for exactly the
 * population whose coverage the employer pays for. The licence object also outlives its
 * validity (see pages/dark-web-scan.vue), so presence alone keeps reporting coverage after
 * the seat lapsed.
 */
export const personHasActiveLicense = (person: LicenseHolder): boolean =>
    !!person?.license && !person.license.expired;

/**
 * Signature tier, read off the plan's `remediation_type` exactly as the API does
 * (`Person::hasSignatureLicense`). Not a slug match: a catalogue that gains another plan
 * carrying the same remediation would be silently demoted by an allow-list, and the UI would
 * then hide an affordance the API allows.
 */
export const personHasSignatureLicense = (person: LicenseHolder): boolean =>
    personHasActiveLicense(person) && person?.license?.remediation_type === 'sst';

/**
 * The live, unassigned seats this person owns that answer `feature`: every spare when no feature
 * is named (or the registry does not publish it), otherwise only those on a plan that reaches it.
 * Assigning a seat below the feature's plan would leave the feature locked behind a seat the
 * person now holds, so the gate and the assignment dialog read this one list rather than two.
 */
export const spareSeatsFor = (person: LicenseHolder, feature?: string | null): License[] => {
    const required = feature ? resolveFeatureGate(feature)?.requiredPlan : undefined;
    return (person?.licenses ?? []).filter(
        (seat: License) =>
            !seat.expired && !seat.assigned_to && (!required || licenseTierRank(seat) >= licenseTierRank(required)),
    );
};

/** A live seat this person owns and has not handed to anyone — assignable instead of sold. */
export const personHasUnassignedSeat = (person: LicenseHolder): boolean => spareSeatsFor(person).length > 0;

/**
 * The one licence gate. `covered` is the feature-level answer a surface already holds (does
 * this plan reach this feature); surfaces that only ask "is this person protected at all"
 * leave it true.
 */
export const resolveLicenseGate = (person: LicenseHolder, covered = true): LicenseGate => {
    if (!personHasActiveLicense(person)) {
        return personHasUnassignedSeat(person) ? 'needs_gift' : 'needs_purchase';
    }
    return covered ? 'allowed' : 'needs_upgrade';
};
